import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { ArrowLeft, Clock, Check, Plus, Play, Pause, RotateCcw, Dumbbell, Heart, Footprints, Sparkles, Timer, X, Search, Trash2, GripVertical, Save, ChevronUp, ChevronDown, Edit2, Folder, Star, TrendingUp, BarChart3 } from 'lucide-react';
import axios from 'axios';
import { API, getLocalISOString, stripHtml } from '../App';
import { toast } from 'sonner';
import HamburgerMenu from '../components/HamburgerMenu';

const PRESET_ROUTINES = [
  {
    id: 'yoga_flow',
    name: 'Yoga Flow',
    description: 'Full body yoga sequence for flexibility and relaxation',
    duration: 30,
    category: 'yoga',
    icon: '🧘',
    exercises: [
      { name: 'Sun Salutation A (Surya Namaskar A)', duration: 5, type: 'flow', instructions: 'Flow through the sequence: Mountain pose → Forward fold → Half lift → Plank → Chaturanga → Upward dog → Downward dog. Breathe with each movement.' },
      { name: 'Warrior I (Virabhadrasana I)', duration: 2, type: 'hold', instructions: 'Step one foot back, bend front knee to 90°. Hips face forward, arms overhead. Keep back leg straight and strong.' },
      { name: 'Warrior II (Virabhadrasana II)', duration: 2, type: 'hold', instructions: 'Open hips to the side, arms extend parallel to floor. Gaze over front fingertips. Keep front knee tracking over ankle.' },
      { name: 'Triangle Pose (Trikonasana)', duration: 2, type: 'hold', instructions: 'Straighten front leg, reach forward then down. Rest hand on shin or floor. Top arm reaches up. Keep both sides of torso long.' },
      { name: 'Downward Facing Dog (Adho Mukha Svanasana)', duration: 3, type: 'hold', instructions: 'Press hands firmly, lift hips high. Heels reach toward floor. Spread fingers wide, relax neck. Pedal feet gently.' },
      { name: 'Pigeon Pose (Eka Pada Rajakapotasana)', duration: 4, type: 'hold', instructions: 'From downward dog, bring knee forward. Extend back leg straight behind. Square hips, fold forward for deeper stretch.' },
      { name: 'Seated Forward Fold (Paschimottanasana)', duration: 3, type: 'hold', instructions: 'Sit with legs extended. Inhale to lengthen spine, exhale to fold forward from hips. Reach for feet or shins.' },
      { name: 'Supine Twist (Supta Matsyendrasana)', duration: 3, type: 'hold', instructions: 'Lie on back, draw one knee to chest. Guide knee across body. Keep both shoulders grounded. Gaze opposite the knee.' },
      { name: 'Corpse Pose (Savasana)', duration: 4, type: 'hold', instructions: 'Lie flat on back, arms by sides palms up. Close eyes, release all muscle tension. Breathe naturally and let go completely.' },
    ]
  },
  {
    id: 'pilates_core',
    name: 'Pilates Core',
    description: 'Core strengthening Pilates routine',
    duration: 25,
    category: 'pilates',
    icon: '💪',
    exercises: [
      { name: 'The Hundred', duration: 3, type: 'reps', instructions: 'Lie on back, lift legs to tabletop. Curl head and shoulders up, extend arms by sides. Pump arms up and down while breathing: inhale 5 counts, exhale 5 counts.' },
      { name: 'Roll Up', duration: 3, type: 'reps', instructions: 'Lie flat, arms overhead. Slowly roll up one vertebra at a time, reaching for toes. Roll back down with control. Keep core engaged throughout.' },
      { name: 'Single Leg Stretch', duration: 3, type: 'reps', instructions: 'Curl up, hold one knee to chest while extending other leg at 45°. Switch legs rhythmically. Keep shoulders lifted and core engaged.' },
      { name: 'Double Leg Stretch', duration: 3, type: 'reps', instructions: 'Curl up, hug knees to chest. Extend arms and legs out in a V-shape, then circle arms back as you draw knees in.' },
      { name: 'Scissors', duration: 3, type: 'reps', instructions: 'Curl up, extend both legs to ceiling. Lower one leg toward floor while pulsing the other toward you. Switch legs in a scissor motion.' },
      { name: 'Swimming', duration: 3, type: 'timed', instructions: 'Lie on stomach, arms extended forward. Lift opposite arm and leg, then switch rapidly like swimming. Keep core engaged, eyes down.' },
      { name: 'Teaser I', duration: 3, type: 'reps', instructions: 'Lie flat, arms overhead. Simultaneously lift torso and legs to form a V-shape. Reach arms parallel to legs. Roll down with control.' },
      { name: 'Seal', duration: 4, type: 'reps', instructions: 'Sit in a ball, hold feet from inside. Roll back and clap feet 3 times at top, roll up and clap 3 times. Use momentum from core.' },
    ]
  },
  {
    id: 'full_stretch',
    name: 'Full Body Stretch',
    description: 'Complete stretching routine for all major muscle groups',
    duration: 20,
    category: 'stretch',
    icon: '🤸',
    exercises: [
      { name: 'Neck Rolls', duration: 2, type: 'timed', instructions: 'Slowly roll head in circles, ear to shoulder, chin to chest. Move gently and pause at tight spots. Switch directions halfway.' },
      { name: 'Cross-Body Shoulder Stretch', duration: 2, type: 'hold', instructions: 'Extend one arm across body at shoulder height. Use opposite hand to gently pull arm closer. Keep shoulder down.' },
      { name: 'Chest Doorway Stretch', duration: 2, type: 'hold', instructions: 'Place forearm on doorframe, elbow at 90°. Step through and rotate body away from arm. Feel stretch across chest and front shoulder.' },
      { name: 'Standing Forward Fold (Uttanasana)', duration: 2, type: 'hold', instructions: 'Stand with feet hip-width apart. Fold forward from hips, let arms hang or grab opposite elbows. Relax neck completely.' },
      { name: 'Standing Quad Stretch', duration: 2, type: 'hold', instructions: 'Stand on one leg, grab opposite foot behind you. Pull heel toward glute. Keep knees together, stand tall.' },
      { name: 'Hip Flexor Stretch', duration: 3, type: 'hold', instructions: 'Kneel on one knee, other foot forward. Tuck tailbone, shift weight forward. Raise same-side arm for deeper stretch.' },
      { name: 'Figure Four Stretch', duration: 3, type: 'hold', instructions: 'Lie on back, cross ankle over opposite knee. Thread hands behind thigh and pull toward chest. Keep hips level.' },
      { name: 'Lying Spinal Twist', duration: 2, type: 'hold', instructions: 'Lie on back, extend arms to T. Drop both knees to one side while keeping shoulders flat. Gaze opposite direction.' },
    ]
  },
  {
    id: 'hip_opener',
    name: 'Hip Opener Flow',
    description: 'Deep hip stretches for flexibility and mobility',
    duration: 25,
    category: 'yoga',
    icon: '🦋',
    exercises: [
      { name: 'Low Lunge (Anjaneyasana)', duration: 3, type: 'hold', instructions: 'Step one foot forward into deep lunge. Lower back knee to floor. Sink hips forward and down. Arms can reach up or rest on front thigh.' },
      { name: 'Lizard Pose (Utthan Pristhasana)', duration: 4, type: 'hold', instructions: 'From low lunge, bring both hands inside front foot. Option to lower to forearms. Keep front foot flat or roll to outer edge.' },
      { name: 'Pigeon Pose (Eka Pada Rajakapotasana)', duration: 5, type: 'hold', instructions: 'Bring front shin parallel to mat (or angled). Extend back leg straight. Square hips and fold forward for deeper stretch.' },
      { name: 'Frog Pose (Mandukasana)', duration: 4, type: 'hold', instructions: 'On all fours, widen knees as far as comfortable. Ankles in line with knees. Lower to forearms, sink hips back.' },
      { name: 'Bound Angle Pose (Baddha Konasana)', duration: 3, type: 'hold', instructions: 'Sit with soles of feet together, knees wide. Hold feet or ankles. Lengthen spine, option to fold forward.' },
      { name: 'Happy Baby (Ananda Balasana)', duration: 3, type: 'hold', instructions: 'Lie on back, grab outer edges of feet. Draw knees toward armpits. Keep tailbone down, rock side to side.' },
      { name: 'Reclined Pigeon (Supta Kapotasana)', duration: 3, type: 'hold', instructions: 'Lie on back, cross one ankle over opposite knee. Thread hands behind thigh and draw toward chest.' },
    ]
  },
  {
    id: 'morning_energize',
    name: 'Morning Energizer',
    description: 'Quick morning routine to wake up your body',
    duration: 15,
    category: 'yoga',
    icon: '☀️',
    exercises: [
      { name: 'Cat-Cow Stretch', duration: 2, type: 'flow', instructions: 'On all fours, alternate arching back (cow) and rounding spine (cat). Move with your breath - inhale for cow, exhale for cat.' },
      { name: 'Sun Salutation A (Surya Namaskar A)', duration: 5, type: 'flow', instructions: 'Flow through: Mountain → Forward fold → Half lift → Plank → Chaturanga → Up dog → Down dog. One breath per movement.' },
      { name: 'Warrior II (Virabhadrasana II)', duration: 2, type: 'hold', instructions: 'Wide stance, front knee bent 90°. Arms extend parallel to floor, gaze over front hand. Strong back leg, open hips.' },
      { name: 'Chair Pose (Utkatasana)', duration: 2, type: 'hold', instructions: 'Feet together, sit back like into a chair. Arms reach up, weight in heels. Keep chest lifted and spine long.' },
      { name: 'Standing Forward Fold (Uttanasana)', duration: 2, type: 'hold', instructions: 'Fold from hips, let head hang heavy. Bend knees if needed. Grab opposite elbows and sway gently.' },
      { name: 'Mountain Pose (Tadasana)', duration: 2, type: 'hold', instructions: 'Stand tall, feet together or hip-width. Arms by sides, shoulders back. Crown of head reaches up, root down through feet.' },
    ]
  },
  {
    id: 'evening_wind_down',
    name: 'Evening Wind Down',
    description: 'Relaxing routine to prepare for restful sleep',
    duration: 20,
    category: 'yoga',
    icon: '🌙',
    exercises: [
      { name: 'Child\'s Pose (Balasana)', duration: 3, type: 'hold', instructions: 'Kneel, sit back on heels. Fold forward, arms extended or by sides. Rest forehead on mat. Breathe deeply into back body.' },
      { name: 'Puppy Pose (Uttana Shishosana)', duration: 3, type: 'hold', instructions: 'From all fours, walk hands forward. Keep hips over knees, chest melts toward floor. Feel stretch through shoulders and upper back.' },
      { name: 'Supine Twist (Supta Matsyendrasana)', duration: 3, type: 'hold', instructions: 'Lie on back, hug one knee. Guide knee across body while extending opposite arm. Keep both shoulders grounded.' },
      { name: 'Reclining Bound Angle (Supta Baddha Konasana)', duration: 4, type: 'hold', instructions: 'Lie on back, soles of feet together, knees fall open. Arms relaxed by sides or on belly. Let gravity open hips.' },
      { name: 'Legs Up The Wall (Viparita Karani)', duration: 4, type: 'hold', instructions: 'Lie with legs extended up a wall. Hips close to wall, arms relaxed. Close eyes and breathe. Excellent for circulation and relaxation.' },
      { name: 'Corpse Pose (Savasana)', duration: 3, type: 'hold', instructions: 'Lie flat, arms by sides palms up, legs slightly apart. Scan body and release all tension. Focus only on breath.' },
    ]
  }
];

// Quick Activities with guided exercises
const ACTIVITY_CARDS = [
  { 
    id: 'walking', 
    name: 'Walking', 
    icon: Footprints, 
    color: 'from-green-600 to-green-800', 
    defaultDuration: 30,
    description: 'Low-impact cardio walking session',
    exercises: [
      { name: 'Warm-up Walk', duration: 5, type: 'timed', instructions: 'Start with a slow, easy pace to warm up your muscles.' },
      { name: 'Brisk Walk', duration: 10, type: 'timed', instructions: 'Increase your pace. Pump your arms naturally.' },
      { name: 'Power Walk', duration: 10, type: 'timed', instructions: 'Walk at your fastest comfortable pace. Engage your core.' },
      { name: 'Cool-down Walk', duration: 5, type: 'timed', instructions: 'Slow down gradually. Let your heart rate return to normal.' },
    ]
  },
  { 
    id: 'yoga', 
    name: 'Yoga', 
    icon: Heart, 
    color: 'from-purple-600 to-purple-800', 
    defaultDuration: 30,
    description: 'Calming yoga flow for flexibility',
    exercises: [
      { name: 'Mountain Pose (Tadasana)', duration: 2, type: 'timed', instructions: 'Stand tall, feet together, arms at sides. Ground through feet, lengthen spine.' },
      { name: 'Forward Fold (Uttanasana)', duration: 2, type: 'timed', instructions: 'Hinge at hips, fold forward. Let head hang, bend knees if needed.' },
      { name: 'Downward Dog', duration: 3, type: 'timed', instructions: 'Hands shoulder-width, feet hip-width. Press hips up and back, heels toward floor.' },
      { name: 'Warrior I', duration: 3, type: 'timed', instructions: 'Step one foot back, front knee bent 90°. Arms overhead, hips square forward.' },
      { name: 'Warrior II', duration: 3, type: 'timed', instructions: 'Open hips to side, arms extended. Gaze over front hand.' },
      { name: 'Triangle Pose', duration: 3, type: 'timed', instructions: 'Straighten front leg, reach forward then down. Top arm reaches up.' },
      { name: 'Tree Pose', duration: 3, type: 'timed', instructions: 'Balance on one leg, other foot on inner thigh or calf. Hands at heart or overhead.' },
      { name: 'Child\'s Pose', duration: 3, type: 'timed', instructions: 'Kneel, sit back on heels, fold forward. Arms extended or by sides.' },
      { name: 'Cat-Cow Stretch', duration: 3, type: 'timed', instructions: 'On all fours, alternate arching back (cat) and dropping belly (cow).' },
      { name: 'Savasana (Corpse Pose)', duration: 5, type: 'timed', instructions: 'Lie flat on back, arms and legs relaxed. Close eyes, breathe deeply.' },
    ]
  },
  { 
    id: 'pilates', 
    name: 'Pilates', 
    icon: Sparkles, 
    color: 'from-pink-600 to-pink-800', 
    defaultDuration: 25,
    description: 'Core-focused Pilates workout',
    exercises: [
      { name: 'The Hundred', duration: 2, type: 'timed', instructions: 'Lie on back, legs tabletop. Pump arms while breathing in for 5, out for 5.' },
      { name: 'Roll Up', duration: 2, type: 'timed', instructions: 'Lie flat, arms overhead. Slowly roll up to seated, then roll back down.' },
      { name: 'Single Leg Circles', duration: 2, type: 'timed', instructions: 'One leg up, draw circles. Keep hips stable, core engaged.' },
      { name: 'Rolling Like a Ball', duration: 2, type: 'timed', instructions: 'Sit in ball position, roll back to shoulders and return. Use momentum.' },
      { name: 'Single Leg Stretch', duration: 3, type: 'timed', instructions: 'Alternate pulling knees to chest. Head and shoulders lifted.' },
      { name: 'Double Leg Stretch', duration: 3, type: 'timed', instructions: 'Extend arms and legs out, then circle arms and hug knees in.' },
      { name: 'Spine Stretch Forward', duration: 2, type: 'timed', instructions: 'Sit tall, legs apart. Round forward reaching past toes, then stack spine up.' },
      { name: 'Swan Dive Prep', duration: 2, type: 'timed', instructions: 'Lie on stomach, hands under shoulders. Lift chest, keeping hips down.' },
      { name: 'Side Kicks', duration: 3, type: 'timed', instructions: 'Lie on side, kick top leg front and back. Keep core stable.' },
      { name: 'Plank Hold', duration: 2, type: 'timed', instructions: 'Hold strong plank position. Body in straight line, core engaged.' },
    ]
  },
  { 
    id: 'stretching', 
    name: 'Stretching', 
    icon: Dumbbell, 
    color: 'from-blue-600 to-blue-800', 
    defaultDuration: 15,
    description: 'Full body stretch routine',
    exercises: [
      { name: 'Neck Rolls', duration: 1, type: 'timed', instructions: 'Gently roll head in circles. 30 seconds each direction.' },
      { name: 'Shoulder Rolls', duration: 1, type: 'timed', instructions: 'Roll shoulders forward, then backward. Release tension.' },
      { name: 'Chest Opener', duration: 2, type: 'timed', instructions: 'Clasp hands behind back, lift chest. Open the front of shoulders.' },
      { name: 'Standing Side Stretch', duration: 2, type: 'timed', instructions: 'Reach one arm overhead, lean to opposite side. Feel the stretch along your side.' },
      { name: 'Standing Quad Stretch', duration: 2, type: 'timed', instructions: 'Stand on one leg, pull other heel to glute. Keep knees together.' },
      { name: 'Standing Hamstring Stretch', duration: 2, type: 'timed', instructions: 'Extend one leg forward, hinge at hips. Feel stretch in back of leg.' },
      { name: 'Hip Flexor Stretch', duration: 2, type: 'timed', instructions: 'Lunge position, back knee down. Push hips forward gently.' },
      { name: 'Seated Spinal Twist', duration: 2, type: 'timed', instructions: 'Sit tall, cross one leg over. Twist toward bent knee, look behind.' },
      { name: 'Figure Four Stretch', duration: 2, type: 'timed', instructions: 'Lie on back, cross ankle over knee. Pull bottom leg toward chest.' },
    ]
  },
];

const ActiveRecovery = ({ user, onLogout }) => {
  const navigate = useNavigate();
  const [selectedActivity, setSelectedActivity] = useState(null);
  const [selectedRoutine, setSelectedRoutine] = useState(null);
  const [customDuration, setCustomDuration] = useState(30);
  const [isTimerActive, setIsTimerActive] = useState(false);
  const [timeRemaining, setTimeRemaining] = useState(0);
  const [currentExerciseIndex, setCurrentExerciseIndex] = useState(0);
  const [completedExercises, setCompletedExercises] = useState([]);
  const [showCustomBuilder, setShowCustomBuilder] = useState(false);
  const [customExercises, setCustomExercises] = useState([]);
  const [availableExercises, setAvailableExercises] = useState([]);
  const [savedRoutines, setSavedRoutines] = useState([]);
  const [saving, setSaving] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [routineName, setRoutineName] = useState('');
  const [routineDescription, setRoutineDescription] = useState('');
  const [showSavedRoutines, setShowSavedRoutines] = useState(false);
  const [favoriteExercises, setFavoriteExercises] = useState([]);
  const [routineStats, setRoutineStats] = useState(null);
  const [showStats, setShowStats] = useState(false);
  const [loadingExercises, setLoadingExercises] = useState(false);

  useEffect(() => {
    loadAvailableExercises();
    loadSavedRoutines();
    loadFavorites();
    loadStats();
  }, []);

  // Retry loading exercises when custom builder is opened if none are loaded
  useEffect(() => {
    if (showCustomBuilder && availableExercises.length === 0 && !loadingExercises) {
      console.log('No exercises loaded, retrying...');
      loadAvailableExercises();
    }
  }, [showCustomBuilder, availableExercises.length, loadingExercises]);

  useEffect(() => {
    let interval;
    if (isTimerActive && timeRemaining > 0) {
      interval = setInterval(() => {
        setTimeRemaining(prev => prev - 1);
      }, 1000);
    } else if (timeRemaining === 0 && isTimerActive) {
      setIsTimerActive(false);
      if (selectedRoutine && currentExerciseIndex < selectedRoutine.exercises.length - 1) {
        setCurrentExerciseIndex(prev => prev + 1);
        setTimeRemaining(selectedRoutine.exercises[currentExerciseIndex + 1].duration * 60);
      }
    }
    return () => clearInterval(interval);
  }, [isTimerActive, timeRemaining, selectedRoutine, currentExerciseIndex]);

  const loadAvailableExercises = async () => {
    setLoadingExercises(true);
    try {
      // Token is automatically added by axios interceptor in App.js
      const response = await axios.get(`${API}/recovery/exercises?limit=200`);
      console.log('Recovery exercises loaded:', response.data.exercises?.length);
      setAvailableExercises(response.data.exercises || []);
      setLoadingExercises(false);
    } catch (error) {
      console.error('Error loading recovery exercises:', error?.response?.status, error?.message);
      // Fallback to regular exercises endpoint
      try {
        const response = await axios.get(`${API}/exercises/all?limit=200`);
        console.log('Fallback exercises loaded:', response.data.exercises?.length);
        const filtered = (response.data.exercises || []).filter(ex => 
          ex.category === 'yoga' || ex.category === 'pilates' || ex.category === 'stretch' ||
          ex.name?.toLowerCase().includes('stretch') || ex.name?.toLowerCase().includes('pose')
        );
        setAvailableExercises(filtered);
        setLoadingExercises(false);
      } catch (err) {
        console.error('Fallback also failed:', err?.response?.status, err?.message);
        setLoadingExercises(false);
      }
    }
  };

  const loadSavedRoutines = async () => {
    try {
      const response = await axios.get(`${API}/recovery/routines`);
      setSavedRoutines(response.data.routines || []);
    } catch (error) {
      console.error('Error loading saved routines:', error);
    }
  };

  const loadFavorites = async () => {
    try {
      const response = await axios.get(`${API}/recovery/favorites`);
      setFavoriteExercises(response.data.favorites || []);
    } catch (error) {
      console.error('Error loading favorites:', error);
    }
  };

  const loadStats = async () => {
    try {
      const response = await axios.get(`${API}/recovery/routines/stats`);
      setRoutineStats(response.data);
    } catch (error) {
      console.error('Error loading stats:', error);
    }
  };

  const toggleFavorite = async (exerciseName) => {
    const isFavorited = favoriteExercises.some(f => f.exercise_name === exerciseName);
    
    try {
      if (isFavorited) {
        await axios.delete(`${API}/recovery/favorites/${encodeURIComponent(exerciseName)}`);
        setFavoriteExercises(prev => prev.filter(f => f.exercise_name !== exerciseName));
        toast.success('Removed from favorites');
      } else {
        const response = await axios.post(`${API}/recovery/favorites/${encodeURIComponent(exerciseName)}`, {});
        setFavoriteExercises(prev => [...prev, response.data.favorite]);
        toast.success('Added to favorites!');
      }
    } catch (error) {
      console.error('Error toggling favorite:', error);
      toast.error('Failed to update favorites');
    }
  };

  const isFavorite = (exerciseName) => {
    return favoriteExercises.some(f => f.exercise_name === exerciseName);
  };

  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const startRoutine = (routine) => {
    setSelectedRoutine(routine);
    setCurrentExerciseIndex(0);
    setTimeRemaining(routine.exercises[0].duration * 60);
    setCompletedExercises([]);
    setIsTimerActive(false);
  };

  const startSavedRoutine = (routine) => {
    const formattedRoutine = {
      ...routine,
      exercises: routine.exercises.map(ex => ({
        name: ex.exercise_name,
        duration: ex.duration_minutes,
        type: 'timed'
      }))
    };
    startRoutine(formattedRoutine);
    setShowSavedRoutines(false);
  };

  const startSimpleActivity = (activity) => {
    // Convert quick activity to a guided routine format
    if (activity.exercises && activity.exercises.length > 0) {
      const routineFromActivity = {
        id: activity.id,
        name: activity.name,
        description: activity.description || `${activity.name} session`,
        duration: activity.defaultDuration,
        icon: '🏃',
        category: activity.name,
        exercises: activity.exercises
      };
      startRoutine(routineFromActivity);
    } else {
      // Fallback for activities without exercises
      setSelectedActivity(activity);
      setSelectedRoutine(null);
      setTimeRemaining(customDuration * 60);
      setIsTimerActive(false);
    }
  };

  const completeCurrentExercise = () => {
    if (selectedRoutine) {
      setCompletedExercises(prev => [...prev, currentExerciseIndex]);
      if (currentExerciseIndex < selectedRoutine.exercises.length - 1) {
        setCurrentExerciseIndex(prev => prev + 1);
        setTimeRemaining(selectedRoutine.exercises[currentExerciseIndex + 1].duration * 60);
        setIsTimerActive(false);
      }
    }
  };

  const logRecoverySession = async () => {
    setSaving(true);
    try {
      let workoutData = {
        workout_type: 'active_recovery',
        date: getLocalISOString(),
        exercises: [],
        completed: true,
        notes: ''
      };

      if (selectedRoutine) {
        workoutData.notes = `${selectedRoutine.name} - ${selectedRoutine.description || ''}`;
        workoutData.exercises = selectedRoutine.exercises.map((ex, idx) => ({
          exercise_name: ex.name,
          muscle_group: selectedRoutine.category || 'Recovery',
          sets: [{
            set_number: 1,
            time: ex.duration,
            completed: completedExercises.includes(idx) || idx <= currentExerciseIndex
          }]
        }));
      } else if (selectedActivity) {
        workoutData.notes = `${selectedActivity.name} - ${customDuration} minutes`;
        workoutData.exercises = [{
          exercise_name: selectedActivity.name,
          muscle_group: 'Active Recovery',
          sets: [{
            set_number: 1,
            time: customDuration,
            completed: true
          }]
        }];
      }

      await axios.post(`${API}/workouts/log`, workoutData);

      toast.success('Recovery session logged!');
      navigate('/hub');
    } catch (error) {
      console.error('Error logging session:', error);
      toast.error('Failed to log session');
    } finally {
      setSaving(false);
    }
  };

  const resetSession = () => {
    setSelectedActivity(null);
    setSelectedRoutine(null);
    setIsTimerActive(false);
    setTimeRemaining(0);
    setCurrentExerciseIndex(0);
    setCompletedExercises([]);
  };

  // Custom Builder Functions
  const addExerciseToRoutine = async (exercise) => {
    const newExercise = {
      exercise_name: exercise.name || exercise.exercise_name,
      duration_minutes: (exercise.duration_seconds || 60) / 60,
      notes: ''
    };
    setCustomExercises([...customExercises, newExercise]);
    
    // Track usage for favorites/stats
    try {
      await axios.post(`${API}/recovery/favorites/${encodeURIComponent(exercise.name || exercise.exercise_name)}/use`, {});
    } catch (error) {
      // Silent fail - just tracking
    }
  };

  const addFavoriteToRoutine = (favorite) => {
    addExerciseToRoutine({
      name: favorite.exercise_name,
      duration_seconds: favorite.duration_seconds || 60
    });
  };

  const removeExerciseFromRoutine = (index) => {
    setCustomExercises(customExercises.filter((_, i) => i !== index));
  };

  const updateExerciseDuration = (index, duration) => {
    const updated = [...customExercises];
    updated[index].duration_minutes = parseFloat(duration) || 0;
    setCustomExercises(updated);
  };

  const moveExercise = (index, direction) => {
    const newIndex = index + direction;
    if (newIndex < 0 || newIndex >= customExercises.length) return;
    
    const updated = [...customExercises];
    [updated[index], updated[newIndex]] = [updated[newIndex], updated[index]];
    setCustomExercises(updated);
  };

  const saveCustomRoutine = async () => {
    if (!routineName.trim()) {
      toast.error('Please enter a routine name');
      return;
    }
    if (customExercises.length === 0) {
      toast.error('Please add at least one exercise');
      return;
    }

    setSaving(true);
    try {
      await axios.post(`${API}/recovery/routines`, {
        name: routineName,
        description: routineDescription,
        category: selectedCategory === 'all' ? 'custom' : selectedCategory,
        exercises: customExercises
      });

      toast.success('Routine saved successfully!');
      setShowCustomBuilder(false);
      setCustomExercises([]);
      setRoutineName('');
      setRoutineDescription('');
      loadSavedRoutines();
    } catch (error) {
      console.error('Error saving routine:', error);
      toast.error('Failed to save routine');
    } finally {
      setSaving(false);
    }
  };

  const deleteRoutine = async (routineId) => {
    try {
      await axios.delete(`${API}/recovery/routines/${routineId}`);
      toast.success('Routine deleted');
      loadSavedRoutines();
    } catch (error) {
      toast.error('Failed to delete routine');
    }
  };

  const filteredExercises = availableExercises.filter(ex => {
    // For multi-word searches, check if all words are present in the name
    const searchWords = searchQuery.toLowerCase().split(' ').filter(w => w.length > 0);
    const exerciseName = (ex.name || '').toLowerCase();
    const exerciseMuscle = (ex.muscle || '').toLowerCase();
    
    const matchesSearch = !searchQuery || 
      searchWords.every(word => exerciseName.includes(word) || exerciseMuscle.includes(word));
    const matchesCategory = selectedCategory === 'all' || ex.category === selectedCategory;
    return matchesSearch && matchesCategory;
  });

  const getTotalDuration = () => {
    return customExercises.reduce((sum, ex) => sum + (ex.duration_minutes || 0), 0);
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-stone-950 via-stone-900 to-stone-950 text-white pb-20 overflow-x-hidden" data-testid="active-recovery-page">
      <HamburgerMenu />
      
      <div className="container mx-auto px-4 py-4 max-w-4xl">
        {/* Page Header */}
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-3">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => {
                if (showStats) {
                  setShowStats(false);
                } else if (showCustomBuilder) {
                  setShowCustomBuilder(false);
                } else if (showSavedRoutines) {
                  setShowSavedRoutines(false);
                } else if (selectedRoutine || selectedActivity) {
                  resetSession();
                } else {
                  navigate('/hub');
                }
              }}
              className="text-stone-300 hover:text-white p-2"
              data-testid="back-btn"
            >
              <ArrowLeft className="w-5 h-5" />
            </Button>
            <h1 className="text-xl md:text-2xl font-bold text-blood-400 tracking-wider">
              {showStats ? 'RECOVERY STATS' : showCustomBuilder ? 'BUILD ROUTINE' : showSavedRoutines ? 'SAVED ROUTINES' : 'ACTIVE RECOVERY'}
            </h1>
          </div>
        </div>
        {/* Statistics View */}
        {showStats ? (
          <div className="space-y-6">
            <Card className="glass border-stone-700 p-6">
              <div className="flex items-center gap-3 mb-6">
                <BarChart3 className="w-6 h-6 text-blood-400" />
                <h2 className="text-xl font-bold text-blood-400">YOUR RECOVERY STATS</h2>
              </div>

              {/* Summary Cards */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
                <div className="bg-gradient-to-br from-blood-900/50 to-stone-900 rounded-xl p-4 text-center">
                  <p className="text-3xl font-bold text-blood-400">{routineStats?.total_routines || 0}</p>
                  <p className="text-sm text-stone-400">Saved Routines</p>
                </div>
                <div className="bg-gradient-to-br from-green-900/50 to-stone-900 rounded-xl p-4 text-center">
                  <p className="text-3xl font-bold text-green-400">{routineStats?.total_completions || 0}</p>
                  <p className="text-sm text-stone-400">Sessions Done</p>
                </div>
                <div className="bg-gradient-to-br from-yellow-900/50 to-stone-900 rounded-xl p-4 text-center">
                  <p className="text-3xl font-bold text-yellow-400">{routineStats?.total_favorite_exercises || 0}</p>
                  <p className="text-sm text-stone-400">Favorites</p>
                </div>
                <div className="bg-gradient-to-br from-purple-900/50 to-stone-900 rounded-xl p-4 text-center">
                  <p className="text-3xl font-bold text-purple-400">
                    {routineStats?.top_exercises?.[0]?.use_count || 0}
                  </p>
                  <p className="text-sm text-stone-400">Top Exercise Uses</p>
                </div>
              </div>

              {/* Most Used Routine */}
              {routineStats?.most_used_routine && (
                <div className="mb-6">
                  <h3 className="text-sm font-bold text-stone-400 mb-3 flex items-center gap-2">
                    <TrendingUp className="w-4 h-4" />
                    MOST USED ROUTINE
                  </h3>
                  <Card className="bg-gradient-to-r from-blood-900/30 to-stone-800 border-blood-700/50 p-4">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="font-bold text-white">{routineStats.most_used_routine.name}</p>
                        <p className="text-sm text-stone-400">
                          {routineStats.most_used_routine.total_duration_minutes?.toFixed(0)} min • 
                          {routineStats.most_used_routine.exercise_count} exercises
                        </p>
                      </div>
                      <div className="text-right">
                        <p className="text-2xl font-bold text-blood-400">{routineStats.most_used_routine.use_count}x</p>
                        <p className="text-xs text-stone-500">completed</p>
                      </div>
                    </div>
                  </Card>
                </div>
              )}

              {/* Top Exercises */}
              {routineStats?.top_exercises?.length > 0 && (
                <div>
                  <h3 className="text-sm font-bold text-stone-400 mb-3 flex items-center gap-2">
                    <Star className="w-4 h-4 text-yellow-500" />
                    YOUR TOP EXERCISES
                  </h3>
                  <div className="space-y-2">
                    {routineStats.top_exercises.map((ex, idx) => (
                      <div key={idx} className="flex items-center justify-between p-3 bg-stone-800/50 rounded-lg">
                        <div className="flex items-center gap-3">
                          <span className="text-lg font-bold text-stone-500 w-6">#{idx + 1}</span>
                          <div>
                            <p className="text-white">{ex.exercise_name}</p>
                            <p className="text-xs text-stone-500 capitalize">{ex.category}</p>
                          </div>
                        </div>
                        <div className="flex items-center gap-2">
                          <span className="text-blood-400 font-bold">{ex.use_count}x</span>
                          <Star className="w-4 h-4 text-yellow-500 fill-yellow-500" />
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Empty State */}
              {!routineStats?.total_routines && !routineStats?.top_exercises?.length && (
                <div className="text-center py-8">
                  <BarChart3 className="w-12 h-12 text-stone-600 mx-auto mb-4" />
                  <p className="text-stone-400 mb-2">No stats yet!</p>
                  <p className="text-sm text-stone-500">Start building and using routines to see your progress</p>
                </div>
              )}
            </Card>
          </div>
        ) : showCustomBuilder ? (
          <div className="space-y-6">
            <Card className="glass border-stone-700 p-6">
              <h2 className="text-xl font-bold text-blood-400 mb-4">BUILD CUSTOM ROUTINE</h2>
              
              {/* Routine Info */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
                <div>
                  <label className="text-sm text-stone-300 mb-1 block">Routine Name *</label>
                  <Input
                    value={routineName}
                    onChange={(e) => setRoutineName(e.target.value)}
                    placeholder="My Yoga Routine"
                    className="bg-stone-900 border-stone-700 text-white"
                    data-testid="routine-name-input"
                  />
                </div>
                <div>
                  <label className="text-sm text-stone-300 mb-1 block">Description</label>
                  <Input
                    value={routineDescription}
                    onChange={(e) => setRoutineDescription(e.target.value)}
                    placeholder="Optional description"
                    className="bg-stone-900 border-stone-700 text-white"
                  />
                </div>
              </div>

              {/* Search & Filter */}
              <div className="flex flex-col sm:flex-row gap-4 mb-4">
                <div className="flex-1 relative">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-stone-400" />
                  <Input
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    placeholder="Search exercises..."
                    className="pl-10 bg-stone-900 border-stone-700 text-white"
                    data-testid="exercise-search"
                  />
                </div>
                <div className="flex gap-2">
                  {['all', 'yoga', 'pilates', 'stretch'].map(cat => (
                    <button
                      key={cat}
                      onClick={() => setSelectedCategory(cat)}
                      className={`px-3 py-2 rounded-lg text-sm font-medium capitalize ${
                        selectedCategory === cat
                          ? 'bg-red-600 text-white'
                          : 'bg-stone-800 text-stone-300 hover:bg-stone-700 hover:text-white'
                      }`}
                    >
                      {cat}
                    </button>
                  ))}
                </div>
              </div>

              {/* Quick Access Favorites */}
              {favoriteExercises.length > 0 && (
                <div className="mb-6">
                  <div className="flex items-center gap-2 mb-2">
                    <Star className="w-4 h-4 text-yellow-500 fill-yellow-500" />
                    <h3 className="text-sm font-bold text-yellow-500">QUICK ACCESS FAVORITES</h3>
                    <span className="text-xs text-stone-400">({favoriteExercises.length})</span>
                  </div>
                  <div className="flex flex-wrap gap-2">
                    {favoriteExercises.slice(0, 8).map((fav, idx) => (
                      <button
                        key={idx}
                        onClick={() => addFavoriteToRoutine(fav)}
                        className="group flex items-center gap-2 px-3 py-2 bg-gradient-to-r from-yellow-900/30 to-amber-900/30 border border-yellow-700/50 rounded-lg hover:border-yellow-500 transition-all"
                        data-testid={`favorite-${idx}`}
                      >
                        <Star className="w-3 h-3 text-yellow-500 fill-yellow-500" />
                        <span className="text-sm text-white">{fav.exercise_name}</span>
                        {fav.use_count > 0 && (
                          <span className="text-xs text-yellow-400 bg-yellow-900/50 px-1.5 py-0.5 rounded">
                            {fav.use_count}x
                          </span>
                        )}
                        <Plus className="w-3 h-3 text-yellow-500 opacity-0 group-hover:opacity-100 transition-opacity" />
                      </button>
                    ))}
                  </div>
                </div>
              )}

              {/* Available Exercises */}
              <div className="mb-6">
                <div className="flex items-center justify-between mb-2">
                  <h3 className="text-sm font-bold text-stone-300">AVAILABLE EXERCISES ({filteredExercises.length})</h3>
                  {availableExercises.length === 0 && !loadingExercises && (
                    <button
                      onClick={loadAvailableExercises}
                      className="text-xs text-red-400 hover:text-red-300 underline"
                    >
                      Reload Exercises
                    </button>
                  )}
                </div>
                
                {loadingExercises ? (
                  <div className="bg-stone-900/50 rounded-lg p-8 text-center">
                    <div className="animate-spin w-8 h-8 border-2 border-red-500 border-t-transparent rounded-full mx-auto mb-2"></div>
                    <p className="text-stone-400">Loading exercises...</p>
                  </div>
                ) : filteredExercises.length === 0 ? (
                  <div className="bg-stone-900/50 rounded-lg p-8 text-center">
                    <p className="text-stone-400 mb-2">
                      {searchQuery ? `No exercises found for "${searchQuery}"` : 'No exercises available'}
                    </p>
                    <button
                      onClick={loadAvailableExercises}
                      className="text-red-400 hover:text-red-300 text-sm underline"
                    >
                      Try reloading
                    </button>
                  </div>
                ) : (
                  <div className="max-h-48 overflow-y-auto bg-stone-900/50 rounded-lg p-2 space-y-1">
                    {filteredExercises.slice(0, 50).map((ex, idx) => (
                      <div
                        key={idx}
                        className="flex items-center justify-between p-2 hover:bg-stone-800 rounded-lg group"
                        data-testid={`available-exercise-${idx}`}
                      >
                        <div 
                          className="flex-1 cursor-pointer"
                          onClick={() => addExerciseToRoutine(ex)}
                        >
                          <p className="text-white text-sm font-medium">{ex.name}</p>
                          <p className="text-stone-400 text-xs">{ex.muscle || ex.category} • {Math.round((ex.duration_seconds || 60) / 60)} min</p>
                        </div>
                        <div className="flex items-center gap-2">
                          <button
                            onClick={(e) => { e.stopPropagation(); toggleFavorite(ex.name); }}
                            className={`p-1.5 rounded-lg transition-all ${
                              isFavorite(ex.name) 
                                ? 'text-yellow-500 bg-yellow-900/30' 
                                : 'text-stone-400 hover:text-yellow-500 hover:bg-stone-700'
                            }`}
                            title={isFavorite(ex.name) ? 'Remove from favorites' : 'Add to favorites'}
                          >
                            <Star className={`w-4 h-4 ${isFavorite(ex.name) ? 'fill-yellow-500' : ''}`} />
                          </button>
                          <button
                            onClick={() => addExerciseToRoutine(ex)}
                            className="p-1.5 rounded-lg text-red-500 hover:bg-stone-700 opacity-0 group-hover:opacity-100 transition-opacity"
                          >
                            <Plus className="w-4 h-4" />
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* Selected Exercises */}
              <div className="mb-6">
                <div className="flex items-center justify-between mb-2">
                  <h3 className="text-sm font-bold text-stone-300">YOUR ROUTINE ({customExercises.length} exercises)</h3>
                  <span className="text-red-400 font-bold">{getTotalDuration().toFixed(1)} min total</span>
                </div>
                
                {customExercises.length === 0 ? (
                  <div className="bg-stone-900/50 rounded-lg p-8 text-center">
                    <p className="text-stone-400">Click exercises above to add them to your routine</p>
                  </div>
                ) : (
                  <div className="bg-stone-900/50 rounded-lg p-2 space-y-2">
                    {customExercises.map((ex, idx) => (
                      <div
                        key={idx}
                        className="flex items-center gap-3 p-3 bg-stone-800/50 rounded-lg"
                        data-testid={`routine-exercise-${idx}`}
                      >
                        <div className="flex flex-col gap-1">
                          <button
                            onClick={() => moveExercise(idx, -1)}
                            disabled={idx === 0}
                            className="p-1 hover:bg-stone-700 rounded disabled:opacity-30 text-stone-300"
                          >
                            <ChevronUp className="w-3 h-3" />
                          </button>
                          <button
                            onClick={() => moveExercise(idx, 1)}
                            disabled={idx === customExercises.length - 1}
                            className="p-1 hover:bg-stone-700 rounded disabled:opacity-30 text-stone-300"
                          >
                            <ChevronDown className="w-3 h-3" />
                          </button>
                        </div>
                        <span className="text-red-400 font-bold w-6">{idx + 1}</span>
                        <div className="flex-1">
                          <p className="text-white text-sm font-medium">{ex.exercise_name}</p>
                        </div>
                        <div className="flex items-center gap-2">
                          <Input
                            type="number"
                            step="0.5"
                            min="0.5"
                            value={ex.duration_minutes}
                            onChange={(e) => updateExerciseDuration(idx, e.target.value)}
                            className="w-20 bg-stone-900 border-stone-700 text-center text-white"
                          />
                          <span className="text-stone-400 text-sm">min</span>
                        </div>
                        <button
                          onClick={() => removeExerciseFromRoutine(idx)}
                          className="p-2 hover:bg-red-900/50 rounded-lg text-red-400"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* Save Button */}
              <div className="flex gap-4">
                <Button
                  onClick={() => setShowCustomBuilder(false)}
                  variant="outline"
                  className="flex-1 border-stone-500 bg-stone-800 text-white hover:bg-stone-700"
                >
                  Cancel
                </Button>
                <Button
                  onClick={saveCustomRoutine}
                  disabled={saving || !routineName.trim() || customExercises.length === 0}
                  className="flex-1 bg-gradient-to-r from-red-600 to-red-700 text-white hover:from-red-500 hover:to-red-600"
                  data-testid="save-routine-btn"
                >
                  <Save className="w-4 h-4 mr-2" />
                  {saving ? 'Saving...' : 'Save Routine'}
                </Button>
              </div>
            </Card>
          </div>
        ) : showSavedRoutines ? (
          // Saved Routines List
          <div className="space-y-4">
            <h2 className="text-2xl font-bold text-blood-400 mb-4 tracking-wider">MY SAVED ROUTINES</h2>
            
            {savedRoutines.length === 0 ? (
              <Card className="glass border-stone-700 p-8 text-center">
                <Folder className="w-12 h-12 text-stone-600 mx-auto mb-4" />
                <p className="text-stone-400 mb-4">No saved routines yet</p>
                <Button
                  onClick={() => {
                    setShowSavedRoutines(false);
                    setShowCustomBuilder(true);
                  }}
                  className="bg-blood-600 hover:bg-blood-700"
                >
                  <Plus className="w-4 h-4 mr-2" />
                  Create Your First Routine
                </Button>
              </Card>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {savedRoutines.map((routine) => (
                  <Card key={routine.id} className="glass border-stone-700 p-4">
                    <div className="flex items-start justify-between mb-2">
                      <div>
                        <h3 className="font-bold text-white">{routine.name}</h3>
                        <p className="text-sm text-stone-400">{routine.description || 'No description'}</p>
                      </div>
                      <button
                        onClick={() => deleteRoutine(routine.id)}
                        className="p-2 hover:bg-red-900/30 rounded-lg text-red-400"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                    <div className="flex items-center gap-4 text-sm text-stone-400 mb-3">
                      <span className="flex items-center gap-1">
                        <Clock className="w-4 h-4" />
                        {routine.total_duration_minutes?.toFixed(0) || 0} min
                      </span>
                      <span>{routine.exercise_count || 0} exercises</span>
                      <span className="capitalize">{routine.category}</span>
                    </div>
                    <Button
                      onClick={() => startSavedRoutine(routine)}
                      className="w-full bg-blood-600 hover:bg-blood-700"
                      data-testid={`start-saved-${routine.id}`}
                    >
                      <Play className="w-4 h-4 mr-2" />
                      Start Routine
                    </Button>
                  </Card>
                ))}
              </div>
            )}
          </div>
        ) : !selectedRoutine && !selectedActivity ? (
          // Selection Screen
          <>
            {/* Quick Activities */}
            <div className="mb-8">
              <h2 className="text-2xl font-bold text-blood-400 mb-4 tracking-wider">QUICK ACTIVITIES</h2>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                {ACTIVITY_CARDS.map((activity) => {
                  const IconComponent = activity.icon;
                  return (
                    <Card 
                      key={activity.id}
                      className="glass border-stone-700 p-4 cursor-pointer hover:border-blood-500/50 transition-all"
                      onClick={() => startSimpleActivity(activity)}
                      data-testid={`activity-${activity.id}`}
                    >
                      <div className={`w-12 h-12 rounded-full bg-gradient-to-r ${activity.color} flex items-center justify-center mb-3`}>
                        <IconComponent className="w-6 h-6 text-white" />
                      </div>
                      <h3 className="font-bold text-white">{activity.name}</h3>
                      <p className="text-sm text-stone-400">{activity.defaultDuration} min default</p>
                    </Card>
                  );
                })}
              </div>
            </div>

            {/* Preset Routines */}
            <div className="mb-8">
              <h2 className="text-2xl font-bold text-blood-400 mb-4 tracking-wider">GUIDED ROUTINES</h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {PRESET_ROUTINES.map((routine) => (
                  <Card 
                    key={routine.id}
                    className="glass border-stone-700 p-4 cursor-pointer hover:border-blood-500/50 transition-all"
                    onClick={() => startRoutine(routine)}
                    data-testid={`routine-${routine.id}`}
                  >
                    <div className="flex items-start gap-4">
                      <div className="text-4xl">{routine.icon}</div>
                      <div className="flex-1">
                        <h3 className="font-bold text-white text-lg">{routine.name}</h3>
                        <p className="text-sm text-stone-400 mb-2">{routine.description}</p>
                        <div className="flex items-center gap-4 text-sm">
                          <span className="text-blood-400 flex items-center gap-1">
                            <Clock className="w-4 h-4" />
                            {routine.duration} min
                          </span>
                          <span className="text-stone-500">
                            {routine.exercises.length} exercises
                          </span>
                        </div>
                      </div>
                    </div>
                  </Card>
                ))}
              </div>
            </div>

            {/* Custom & Saved Routines */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <Card className="glass border-stone-700 p-6 text-center">
                <Button
                  onClick={() => setShowCustomBuilder(true)}
                  className="w-full bg-gradient-to-r from-blood-700 to-blood-900 hover:from-blood-600 hover:to-blood-800 text-white font-bold py-4"
                  data-testid="custom-builder-btn"
                >
                  <Plus className="w-5 h-5 mr-2" />
                  BUILD CUSTOM ROUTINE
                </Button>
              </Card>
              <Card className="glass border-stone-700 p-6 text-center">
                <Button
                  onClick={() => setShowSavedRoutines(true)}
                  className="w-full bg-gradient-to-r from-purple-700 to-purple-900 hover:from-purple-600 hover:to-purple-800 text-white font-bold py-4"
                  data-testid="saved-routines-btn"
                >
                  <Folder className="w-5 h-5 mr-2" />
                  MY SAVED ROUTINES ({savedRoutines.length})
                </Button>
              </Card>
              <Card className="glass border-stone-700 p-6 text-center">
                <Button
                  onClick={() => { loadStats(); setShowStats(true); }}
                  className="w-full bg-gradient-to-r from-amber-700 to-amber-900 hover:from-amber-600 hover:to-amber-800 text-white font-bold py-4"
                  data-testid="stats-btn"
                >
                  <BarChart3 className="w-5 h-5 mr-2" />
                  VIEW STATS
                </Button>
              </Card>
            </div>
          </>
        ) : selectedRoutine ? (
          // Routine In Progress
          <div className="space-y-6">
            <Card className="glass border-stone-700 p-6 text-center">
              <div className="text-4xl mb-2">{selectedRoutine.icon || '🎯'}</div>
              <h2 className="text-2xl font-bold text-blood-400 mb-1">{selectedRoutine.name}</h2>
              <p className="text-stone-400 mb-4">{selectedRoutine.description}</p>
              
              {/* Current Exercise */}
              <div className="bg-stone-800/50 rounded-xl p-6 mb-4">
                <p className="text-sm text-stone-400 mb-2">
                  Exercise {currentExerciseIndex + 1} of {selectedRoutine.exercises.length}
                </p>
                <h3 className="text-2xl font-bold text-white mb-3 uppercase tracking-wider">
                  {selectedRoutine.exercises[currentExerciseIndex].name}
                </h3>
                
                {/* Exercise Description */}
                {selectedRoutine.exercises[currentExerciseIndex].instructions && (
                  <div className="bg-stone-900/50 rounded-lg p-3 mb-4 text-left border border-stone-700">
                    <p className="text-stone-300 text-sm leading-relaxed">
                      {stripHtml(selectedRoutine.exercises[currentExerciseIndex].instructions)}
                    </p>
                  </div>
                )}
                
                {/* Default instructions if none provided */}
                {!selectedRoutine.exercises[currentExerciseIndex].instructions && (
                  <div className="bg-stone-900/50 rounded-lg p-3 mb-4 text-left border border-stone-700">
                    <p className="text-stone-400 text-sm italic">
                      Hold the pose with steady breathing. Focus on form over speed.
                    </p>
                  </div>
                )}
                
                <div className="text-5xl font-mono font-bold text-blood-400 mb-4">
                  {formatTime(timeRemaining)}
                </div>
                
                <div className="flex justify-center gap-4 mb-4">
                  <Button
                    onClick={() => setIsTimerActive(!isTimerActive)}
                    className={isTimerActive ? 'bg-amber-600 hover:bg-amber-700' : 'bg-green-600 hover:bg-green-700'}
                    data-testid="timer-toggle"
                  >
                    {isTimerActive ? <Pause className="w-5 h-5 mr-2" /> : <Play className="w-5 h-5 mr-2" />}
                    {isTimerActive ? 'Pause' : 'Start'}
                  </Button>
                  <Button
                    onClick={completeCurrentExercise}
                    className="bg-blood-600 hover:bg-blood-700"
                    data-testid="complete-exercise"
                  >
                    <Check className="w-5 h-5 mr-2" />
                    Complete
                  </Button>
                </div>
                
                {/* YouTube Tutorial Link */}
                <a
                  href={`https://www.youtube.com/results?search_query=${encodeURIComponent(selectedRoutine.exercises[currentExerciseIndex].name + ' exercise tutorial')}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-2 text-sm text-red-400 hover:text-red-300 transition-colors"
                >
                  <Play className="w-4 h-4" />
                  Watch Tutorial on YouTube
                </a>
              </div>
              
              {/* Progress */}
              <div className="flex gap-2 justify-center flex-wrap">
                {selectedRoutine.exercises.map((ex, idx) => (
                  <div
                    key={idx}
                    className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold ${
                      completedExercises.includes(idx) 
                        ? 'bg-green-600 text-white'
                        : idx === currentExerciseIndex
                        ? 'bg-blood-600 text-white'
                        : 'bg-stone-700 text-stone-400'
                    }`}
                    title={ex.name}
                  >
                    {completedExercises.includes(idx) ? <Check className="w-4 h-4" /> : idx + 1}
                  </div>
                ))}
              </div>
            </Card>
            
            {/* Action Buttons */}
            <div className="flex gap-4">
              <Button
                onClick={resetSession}
                variant="outline"
                className="flex-1 border-stone-500 text-stone-200 bg-stone-800 hover:bg-stone-700"
              >
                <RotateCcw className="w-4 h-4 mr-2" />
                Reset
              </Button>
              <Button
                onClick={logRecoverySession}
                disabled={saving}
                className="flex-1 bg-gradient-to-r from-blood-600 to-blood-700 text-white"
                data-testid="log-session"
              >
                <Check className="w-4 h-4 mr-2" />
                {saving ? 'Logging...' : 'Log Session'}
              </Button>
            </div>
          </div>
        ) : selectedActivity ? (
          // Simple Activity Timer
          <div className="space-y-6">
            <Card className="glass border-stone-700 p-6 text-center">
              <div className={`w-20 h-20 rounded-full bg-gradient-to-r ${selectedActivity.color} flex items-center justify-center mx-auto mb-4`}>
                {(() => {
                  const IconComponent = selectedActivity.icon;
                  return <IconComponent className="w-10 h-10 text-white" />;
                })()}
              </div>
              <h2 className="text-2xl font-bold text-blood-400 mb-4">{selectedActivity.name}</h2>
              
              {/* Duration Selector */}
              <div className="mb-6">
                <p className="text-stone-400 mb-2">Duration (minutes)</p>
                <div className="flex justify-center gap-2 flex-wrap">
                  {[10, 15, 20, 30, 45, 60].map(mins => (
                    <button
                      key={mins}
                      onClick={() => {
                        setCustomDuration(mins);
                        setTimeRemaining(mins * 60);
                      }}
                      className={`px-4 py-2 rounded-lg font-bold ${
                        customDuration === mins
                          ? 'bg-blood-600 text-white'
                          : 'bg-stone-800 text-stone-400 hover:bg-stone-700'
                      }`}
                    >
                      {mins}
                    </button>
                  ))}
                </div>
              </div>
              
              {/* Timer */}
              <div className="text-6xl font-mono font-bold text-blood-400 mb-6">
                {formatTime(timeRemaining)}
              </div>
              
              <div className="flex justify-center gap-4">
                <Button
                  onClick={() => setIsTimerActive(!isTimerActive)}
                  className={isTimerActive ? 'bg-amber-600 hover:bg-amber-700' : 'bg-green-600 hover:bg-green-700'}
                  data-testid="timer-toggle"
                >
                  {isTimerActive ? <Pause className="w-5 h-5 mr-2" /> : <Play className="w-5 h-5 mr-2" />}
                  {isTimerActive ? 'Pause' : 'Start'}
                </Button>
                <Button
                  onClick={() => setTimeRemaining(customDuration * 60)}
                  variant="outline"
                  className="border-stone-600"
                >
                  <RotateCcw className="w-5 h-5 mr-2" />
                  Reset
                </Button>
              </div>
            </Card>
            
            {/* Log Button */}
            <Button
              onClick={logRecoverySession}
              disabled={saving}
              className="w-full bg-gradient-to-r from-blood-600 to-blood-700 py-6 text-lg font-bold"
              data-testid="log-activity"
            >
              <Check className="w-5 h-5 mr-2" />
              {saving ? 'Logging...' : 'Log Activity'}
            </Button>
          </div>
        ) : null}
      </div>
    </div>
  );
};

export default ActiveRecovery;
